import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { forwardRef, ReactNode, useState } from 'react';
import { cn } from '../../utilities/cn';
import { Tooltip } from '@mui/material';
import { ultraTableStyles } from '../tables/ultraTable/styles/ultratable.styles';
import { zIndexes } from '../../styling/zIndexes';
import { HiOutlineChevronUpDown } from 'react-icons/hi2';
import SearchBar from './inputs/SearchBar';

interface GenericDropdownProps<T> {
  className?: string;
  label?: ReactNode;
  dropdownButton?: boolean;
  placeholder?: string;
  data: T[];
  getDisplayValue: (item: T) => string;
  /** Optional rich option content; search still uses `getDisplayValue`. */
  getDisplayNode?: (item: T) => ReactNode;
  selected: T[] | T;
  setSelected: (value: T[] | T) => void;
  tooltip?: string;
  dropdownClassName?: string;
  searchBar?: boolean;
  truncateLabel?: boolean;
  fullWidth?: boolean;
}

const GenericDropdown = forwardRef<HTMLDivElement, GenericDropdownProps<any>>(
  <T,>(props: GenericDropdownProps<T>, ref: React.Ref<HTMLDivElement>) => {
    const classes = ultraTableStyles();

    const [searchValue, setSearchValue] = useState('');

    const filteredData = props.data.filter((data) =>
      props
        .getDisplayValue(data)
        .toLowerCase()
        .includes(searchValue.toLowerCase()),
    );

    const optionItems = filteredData.map((item, index) => {
      const isSelected = Array.isArray(props.selected)
        ? props.selected.includes(item)
        : props.selected === item;

      return (
        <MenuItem key={index}>
          <button
            type="button"
            onClick={() => props.setSelected(item)}
            className={cn(
              'group flex w-full items-center gap-2 rounded-lg px-3 text-left data-[focus]:bg-blue-50',
              props.getDisplayNode ? 'min-h-9 py-2' : 'py-1.5',
              isSelected && 'bg-blue-100',
            )}
          >
            <div
              className={cn(
                'text-sm/6',
                props.getDisplayNode && 'flex items-center',
              )}
            >
              {props.getDisplayNode?.(item) ?? props.getDisplayValue(item)}
            </div>
          </button>
        </MenuItem>
      );
    });

    const menuButton = (
      <MenuButton
        className={cn(
          'inline-flex items-center gap-2 rounded-md bg-mainblue fill-white/60 px-3 py-1.5 font-sans text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:outline-none data-[hover]:bg-gray-700 data-[open]:bg-gray-700 data-[focus]:outline-1 data-[focus]:outline-white',
          props.className,
        )}
      >
        <span
          className={cn(
            'min-w-0 flex-1 text-left leading-none',
            props.truncateLabel !== false
              ? 'truncate'
              : 'flex items-center',
          )}
          title={typeof props.label === 'string' ? props.label : undefined}
        >
          {props.label || 'Options'}
        </span>
        <HiOutlineChevronUpDown className="h-5 w-5 shrink-0" />
      </MenuButton>
    );

    return (
      <div
        ref={ref}
        className={cn('min-w-0 font-sans', props.fullWidth && 'w-full')}
      >
        <Menu>
          {props.tooltip ? (
            <Tooltip
              classes={{ tooltip: classes.columnHeadTooltip }}
              title={props.tooltip}
              placement="top"
              enterDelay={500}
            >
              {menuButton}
            </Tooltip>
          ) : (
            menuButton
          )}

          <MenuItems
            portal
            modal={false}
            transition
            anchor={{ to: 'bottom start', gap: 4, padding: 16 }}
            style={{ zIndex: zIndexes.floatingPanel }}
            className={cn(
              'min-w-[var(--button-width)] origin-top-left rounded-xl border border-black/5 bg-white text-sm/6 text-black shadow-lg transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0',
              props.searchBar
                ? 'flex flex-col overflow-hidden! p-0'
                : 'p-1',
              props.dropdownClassName,
            )}
          >
            {props.searchBar ? (
              <>
                <div className="shrink-0 border-b border-gray-200 bg-white px-2 py-2">
                  <SearchBar searchHandler={setSearchValue} />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
                  {optionItems}
                </div>
              </>
            ) : (
              optionItems
            )}
          </MenuItems>
        </Menu>
      </div>
    );
  },
);

export default GenericDropdown;
